import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AlertCircle, RefreshCcw, X, ArrowRight } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../Auth';
import {
  DEFAULT_PROJECT_PRIORITY_VALUE,
  PROJECT_PRIORITY_OPTIONS,
  getProjectPriorityInfo,
} from '../../utils/projectPriority';

const apiBaseUrl = process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_API_URL || 'http://localhost:8000'
  : `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:8000';
const focusMentionPattern = /@\[(circle|role|project):(\d+)\|([^\]]+)\]/g;
const WEEK_HISTORY_COUNT = 6;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toStartOfDay = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const toEndOfDay = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

const addDaysToDate = (value, days) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setDate(date.getDate() + days);
  return date;
};

const getIsoWeekStart = (value) => {
  const date = toStartOfDay(value);
  const diff = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - diff);
  return date;
};

const getIsoWeekEnd = (value) => toEndOfDay(addDaysToDate(getIsoWeekStart(value), 6));

const formatDateKeyValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const countCalendarDays = (start, end) => {
  const from = toStartOfDay(start);
  const to = toStartOfDay(end);
  if (from > to) return 0;
  return Math.floor((to - from) / MS_PER_DAY) + 1;
};

const countBusinessDays = (start, end) => {
  const from = toStartOfDay(start);
  const to = toStartOfDay(end);
  if (from > to) return 0;
  let count = 0;
  for (let cursor = new Date(from); cursor <= to; cursor = addDaysToDate(cursor, 1)) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
};

const minDateValue = (left, right) => (left <= right ? left : right);

const MeetingPanelTactical = ({
  activeMeeting,
  selectedCircle,
  meetingError,
  meetingParticipants,
  meetingPoints,
  preparedPoints = [],
  preparedPointsLoading = false,
  selectedMeetingPointId,
  selectedMeetingProjectId,
  meetingProjectReviews = [],
  meetingPointComposer,
  connectedUsers = [],
  realtimeConnected,
  presenceCount,
  employees = [],
  projects = [],
  circles = [],
  projectSprints = {},
  onFetchProjectSprints,
  onFetchProjectMetrics,
  onFetchProjectObjectives,
  projectMetricsById,
  projectMetricsError,
  meetingPhase,
  onSelectMeetingPhase,
  userIsSecretary,
  onCloseMeeting,
  onTransferSecretary,
  onInviteParticipant,
  onAddMeetingPoint,
  onImportMeetingPoint,
  onSelectMeetingPoint,
  onPointDecision,
  onPointNotesChange,
  onPointComposerUpdate,
  onCreatePointTask,
  onCreatePointObjective,
  onSelectMeetingProject,
  onFetchProjectReviews,
  onUpdateProjectReview,
  metricsRange,
  metricsRangeValid,
}) => {
  const DAILY_HOURS = 7.8;
  const { getAuthHeaders } = useAuth();
  const [newPointTitle, setNewPointTitle] = useState('');
  const [inviteEmployeeId, setInviteEmployeeId] = useState('');
  const [preparedPointId, setPreparedPointId] = useState('');
  const [pointNotesDraft, setPointNotesDraft] = useState('');
  const [objectiveProjectSearch, setObjectiveProjectSearch] = useState('');
  const [circleMetricsById, setCircleMetricsById] = useState({});
  const [circleMetricsLoading, setCircleMetricsLoading] = useState({});
  const [circleMetricsError, setCircleMetricsError] = useState(null);
  const [projectObjectives, setProjectObjectives] = useState([]);
  const [objectivesLoading, setObjectivesLoading] = useState(false);
  const [objectivesError, setObjectivesError] = useState(null);
  const [projectFocusEntries, setProjectFocusEntries] = useState([]);
  const [projectFocusLoading, setProjectFocusLoading] = useState(false);
  const [projectFocusError, setProjectFocusError] = useState(null);
  const [projectHoursByEmployee, setProjectHoursByEmployee] = useState({});
  const [projectHoursLoading, setProjectHoursLoading] = useState(false);
  const [projectHoursError, setProjectHoursError] = useState(null);
  const [projectPriorityDraft, setProjectPriorityDraft] = useState('');
  const [projectPrioritySaveState, setProjectPrioritySaveState] = useState('idle');
  const isMountedRef = useRef(true);
  const meetingCircleKeyRef = useRef('');
  const pointNotesTypingRef = useRef(false);
  const pointNotesTypingTimer = useRef(null);
  const metricsFetchInFlightRef = useRef(new Set());
  const fetchProjectMetricsFnRef = useRef(onFetchProjectMetrics);
  const circleMetricsByIdRef = useRef({});
  const projectMetricsByIdRef = useRef(projectMetricsById || {});
  const projectPriorityRequestRef = useRef(0);

  const meetingCircleId = activeMeeting?.circle_id || selectedCircle?.id;
  const meetingCircleKey = meetingCircleId ? String(meetingCircleId) : '';
  const childrenByParent = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (!circle?.parent_id) return;
      if (!map[circle.parent_id]) map[circle.parent_id] = [];
      map[circle.parent_id].push(circle);
    });
    return map;
  }, [circles]);

  const getDescendantIds = useCallback(
    (circleId) => {
      const ids = new Set();
      const stack = [circleId];
      while (stack.length > 0) {
        const current = stack.pop();
        if (!current || ids.has(current)) continue;
        ids.add(current);
        (childrenByParent[current] || []).forEach((child) => {
          if (child?.id) stack.push(child.id);
        });
      }
      return ids;
    },
    [childrenByParent]
  );

  const circleProjects = useMemo(() => {
    let list = projects || [];
    if (meetingCircleId) {
      const scopeIds = getDescendantIds(Number(meetingCircleId));
      list = (projects || []).filter((project) => {
        if (!project?.circle_id) return false;
        return scopeIds.has(Number(project.circle_id));
      });
    }
    return list.sort((a, b) => {
      const left = Number(a.project_number || a.id || 0);
      const right = Number(b.project_number || b.id || 0);
      return left - right;
    });
  }, [projects, meetingCircleId, getDescendantIds]);

  const meetingProjects = useMemo(() => {
    const inProgressOnly = circleProjects.filter((project) => {
      const status = String(project?.status || '').toLowerCase();
      return status === 'in_progress';
    });
    return inProgressOnly.sort((a, b) => {
      const left = Number(a.project_number || a.id || 0);
      const right = Number(b.project_number || b.id || 0);
      return left - right;
    });
  }, [circleProjects]);

  const selectedProject = useMemo(
    () => meetingProjects.find((project) => String(project.id) === String(selectedMeetingProjectId)) || null,
    [meetingProjects, selectedMeetingProjectId]
  );
  const projectIdsKey = useMemo(
    () => meetingProjects.map((project) => project.id).join(','),
    [meetingProjects]
  );
  const reviewByProjectId = useMemo(() => {
    const map = {};
    (meetingProjectReviews || []).forEach((entry) => {
      if (entry?.project_id === undefined || entry?.project_id === null) return;
      map[String(entry.project_id)] = entry;
    });
    return map;
  }, [meetingProjectReviews]);
  const currentProjectReview = selectedProject
    ? reviewByProjectId[String(selectedProject.id)]?.current || null
    : null;
  const getMeetingProjectPriorityInfo = useCallback((project) => {
    if (!project) return null;
    const currentPriority = reviewByProjectId[String(project.id)]?.current?.priority;
    return getProjectPriorityInfo(
      currentPriority !== null && currentPriority !== undefined ? currentPriority : project.pilotage_priority,
      DEFAULT_PROJECT_PRIORITY_VALUE
    );
  }, [reviewByProjectId]);
  const formatProjectSearchLabel = useCallback((project) => {
    if (!project) return '';
    const projectNumber = project.project_number || project.id;
    return `#${projectNumber} - ${project.name || `Projet #${project.id}`}`;
  }, []);

  const selectedPoint = meetingPoints.find((p) => String(p.id) === String(selectedMeetingPointId)) || null;
  const defaultTaskForm = useMemo(() => ({ name: '', owner_id: '' }), []);
  const defaultObjectiveForm = useMemo(
    () => ({
      name: '',
      project_id: selectedMeetingProjectId ? String(selectedMeetingProjectId) : '',
      owner_id: '',
      effort: '1',
      description: '',
    }),
    [selectedMeetingProjectId]
  );
  const composerMode = meetingPointComposer?.mode === 'task' || meetingPointComposer?.mode === 'objective'
    ? meetingPointComposer.mode
    : null;
  const composerPointId = meetingPointComposer?.point_id !== undefined && meetingPointComposer?.point_id !== null
    ? String(meetingPointComposer.point_id)
    : null;
  const composerMatchesPoint = Boolean(selectedPoint?.id) && String(selectedPoint.id) === String(composerPointId || '');
  const showTaskModal = composerMatchesPoint && composerMode === 'task';
  const showObjectiveModal = composerMatchesPoint && composerMode === 'objective';
  const taskForm = useMemo(
    () => ({
      name: meetingPointComposer?.task_form?.name || '',
      owner_id: meetingPointComposer?.task_form?.owner_id !== undefined && meetingPointComposer?.task_form?.owner_id !== null
        ? String(meetingPointComposer.task_form.owner_id)
        : '',
    }),
    [meetingPointComposer]
  );
  const objectiveForm = useMemo(
    () => ({
      name: meetingPointComposer?.objective_form?.name || '',
      project_id: meetingPointComposer?.objective_form?.project_id !== undefined && meetingPointComposer?.objective_form?.project_id !== null
        ? String(meetingPointComposer.objective_form.project_id)
        : (selectedMeetingProjectId ? String(selectedMeetingProjectId) : ''),
      owner_id: meetingPointComposer?.objective_form?.owner_id !== undefined && meetingPointComposer?.objective_form?.owner_id !== null
        ? String(meetingPointComposer.objective_form.owner_id)
        : '',
      effort: meetingPointComposer?.objective_form?.effort !== undefined && meetingPointComposer?.objective_form?.effort !== null
        ? String(meetingPointComposer.objective_form.effort)
        : '1',
      description: meetingPointComposer?.objective_form?.description || '',
    }),
    [meetingPointComposer, selectedMeetingProjectId]
  );
  const objectiveProjectOptions = useMemo(
    () => (Array.isArray(projects) ? projects : []).map((project) => ({
      id: String(project.id),
      label: formatProjectSearchLabel(project),
    })),
    [projects, formatProjectSearchLabel]
  );
  const selectedObjectiveProjectLabel = useMemo(() => {
    if (!objectiveForm.project_id) return '';
    const selected = (projects || []).find((project) => String(project.id) === String(objectiveForm.project_id));
    return selected ? formatProjectSearchLabel(selected) : '';
  }, [projects, objectiveForm.project_id, formatProjectSearchLabel]);
  const objectiveProjectDatalistId = useMemo(
    () => `objective-project-picker-${activeMeeting?.id || 'default'}`,
    [activeMeeting?.id]
  );
  const visibleMeetingPoints = useMemo(
    () => meetingPoints.filter((point) => (point.status || '').toLowerCase() !== 'closed'),
    [meetingPoints]
  );
  const selectedPointActions = Array.isArray(selectedPoint?.actions) ? selectedPoint.actions : [];

  const activeEmployees = useMemo(
    () => (employees || []).filter((emp) => emp?.is_active !== false),
    [employees]
  );
  const meetingParticipantIds = useMemo(
    () => new Set((meetingParticipants || []).map((participant) => String(participant.id))),
    [meetingParticipants]
  );
  const isParticipantConnected = useCallback(
    (participantId) => connectedUsers.some((u) => {
      const connectedId = u?.employee_id ?? u?.user_id ?? u?.id;
      return String(connectedId) === String(participantId);
    }),
    [connectedUsers]
  );
  const availableInvitees = useMemo(
    () => activeEmployees.filter((emp) => !meetingParticipantIds.has(String(emp.id))),
    [activeEmployees, meetingParticipantIds]
  );
  const employeeById = useMemo(() => {
    const map = {};
    (employees || []).forEach((emp) => {
      if (emp?.id === undefined || emp?.id === null) return;
      map[String(emp.id)] = emp;
    });
    return map;
  }, [employees]);
  const circleNameById = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (circle?.id === undefined || circle?.id === null) return;
      map[String(circle.id)] = circle.name || `Role #${circle.id}`;
    });
    return map;
  }, [circles]);
  const getEmployeeName = useCallback(
    (id) => activeEmployees.find((emp) => String(emp.id) === String(id))?.name || id,
    [activeEmployees]
  );
  const getProjectName = useCallback(
    (id) => (projects || []).find((proj) => String(proj.id) === String(id))?.name || id,
    [projects]
  );
  const secretary = meetingParticipants.find((p) => String(p.id) === String(activeMeeting?.created_by));
  const effectiveMeetingPhase = meetingPhase || 'inclusion';
  const meetingPhases = [
    { key: 'inclusion', label: "Tour d'inclusion", helper: 'Disponibilité des membres', badge: 'Inclusion' },
    { key: 'metrics', label: 'Revue des indicateurs', helper: 'Metrics du cercle', badge: 'Indicateurs' },
    { key: 'projects', label: 'Revue des projets', helper: 'Parcours des projets', badge: 'Projets' },
    { key: 'agenda', label: "Création de l'ordre du jour", helper: 'Points a traiter', badge: 'Ordre du jour' },
    { key: 'closure', label: 'Clôture', helper: 'Fin de réunion', badge: 'Clôture' },
  ];
  const meetingPhaseInfo = meetingPhases.find((phase) => phase.key === effectiveMeetingPhase) || meetingPhases[0];
  const showInclusion = effectiveMeetingPhase === 'inclusion';
  const showMetrics = effectiveMeetingPhase === 'metrics';
  const showProjects = effectiveMeetingPhase === 'projects';
  const showAgenda = effectiveMeetingPhase === 'agenda';
  const showClosure = effectiveMeetingPhase === 'closure';
  const showProjectSidebar = showProjects;

  useEffect(() => {
    if (!selectedMeetingProjectId) return;
    if (!meetingProjects.length) return;
    const exists = meetingProjects.some((project) => String(project.id) === String(selectedMeetingProjectId));
    if (!exists && userIsSecretary && onSelectMeetingProject) {
      onSelectMeetingProject(null);
    }
  }, [selectedMeetingProjectId, meetingProjects, userIsSecretary, onSelectMeetingProject]);

  useEffect(() => {
    if (!projectIdsKey || !onFetchProjectReviews) return;
    onFetchProjectReviews(meetingProjects.map((project) => project.id));
  }, [projectIdsKey, meetingProjects, onFetchProjectReviews]);

  useEffect(() => {
    if (!selectedProject) {
      setProjectPriorityDraft('');
      setProjectPrioritySaveState('idle');
      return;
    }
    const savedPriority = currentProjectReview?.priority;
    const resolvedPriority = savedPriority !== null && savedPriority !== undefined
      ? savedPriority
      : selectedProject.pilotage_priority ?? DEFAULT_PROJECT_PRIORITY_VALUE;
    setProjectPriorityDraft(String(resolvedPriority));
    setProjectPrioritySaveState('idle');
  }, [selectedProject, currentProjectReview?.priority]);

  useEffect(() => {
    if (!showObjectiveModal) {
      setObjectiveProjectSearch('');
      return;
    }
    if (!selectedObjectiveProjectLabel) {
      setObjectiveProjectSearch('');
      return;
    }
    setObjectiveProjectSearch(selectedObjectiveProjectLabel);
  }, [showObjectiveModal, selectedObjectiveProjectLabel]);

  useEffect(() => {
    if (!selectedMeetingProjectId || !onFetchProjectSprints) return;
    onFetchProjectSprints(Number(selectedMeetingProjectId));
  }, [selectedMeetingProjectId, onFetchProjectSprints]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    meetingCircleKeyRef.current = meetingCircleKey;
    metricsFetchInFlightRef.current = new Set();
    setCircleMetricsById({});
    setCircleMetricsLoading({});
    setCircleMetricsError(null);
  }, [meetingCircleKey]);

  useEffect(() => {
    fetchProjectMetricsFnRef.current = onFetchProjectMetrics;
  }, [onFetchProjectMetrics]);

  useEffect(() => {
    circleMetricsByIdRef.current = circleMetricsById || {};
  }, [circleMetricsById]);

  useEffect(() => {
    projectMetricsByIdRef.current = projectMetricsById || {};
  }, [projectMetricsById]);

  useEffect(() => {
    if (!showMetrics || !circleProjects.length) return;
    const fetcher = fetchProjectMetricsFnRef.current;
    if (typeof fetcher !== 'function') return;
    let disposed = false;
    const scopeKey = meetingCircleKey;
    const loadProjectMetricsWithTimeout = async (projectId, timeoutMs = 35000) => {
      let timeoutId = null;
      try {
        return await Promise.race([
          Promise.resolve(fetchProjectMetricsFnRef.current(projectId)),
          new Promise((_, reject) => {
            timeoutId = setTimeout(() => {
              reject(new Error('Timeout while fetching project metrics'));
            }, timeoutMs);
          }),
        ]);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    const loadOneProject = async (project) => {
      const key = String(project.id);
      if (!isMountedRef.current || meetingCircleKeyRef.current !== scopeKey || disposed) return;
      metricsFetchInFlightRef.current.add(key);
      setCircleMetricsLoading((prev) => ({ ...prev, [key]: true }));
      try {
        let data = null;
        let attempts = 0;
        while (attempts < 2) {
          attempts += 1;
          try {
            data = await loadProjectMetricsWithTimeout(project.id);
            break;
          } catch (retryErr) {
            if (attempts >= 2) throw retryErr;
          }
        }
        if (!isMountedRef.current || meetingCircleKeyRef.current !== scopeKey || disposed) return;
        setCircleMetricsById((prev) => ({ ...prev, [key]: Array.isArray(data) ? data : [] }));
      } catch (err) {
        if (!isMountedRef.current || meetingCircleKeyRef.current !== scopeKey || disposed) return;
        setCircleMetricsError(err?.message || 'Erreur lors du chargement des indicateurs du cercle');
        setCircleMetricsById((prev) => ({ ...prev, [key]: [] }));
      } finally {
        metricsFetchInFlightRef.current.delete(key);
        if (!isMountedRef.current || meetingCircleKeyRef.current !== scopeKey || disposed) return;
        setCircleMetricsLoading((prev) => ({ ...prev, [key]: false }));
      }
    };

    const pump = () => {
      if (disposed || !isMountedRef.current || meetingCircleKeyRef.current !== scopeKey) return;
      const sharedMetrics = projectMetricsByIdRef.current || {};
      const localMetrics = circleMetricsByIdRef.current || {};
      const pending = circleProjects.filter((project) => {
        const key = String(project.id);
        if (metricsFetchInFlightRef.current.has(key)) return false;
        const hasSharedMetrics = Object.prototype.hasOwnProperty.call(sharedMetrics, key);
        const hasLocalMetrics = Object.prototype.hasOwnProperty.call(localMetrics, key);
        return !hasSharedMetrics && !hasLocalMetrics;
      });
      if (pending.length === 0) return;
      setCircleMetricsError(null);
      pending.slice(0, 4).forEach((project) => {
        loadOneProject(project);
      });
    };

    pump();
    const intervalId = setInterval(pump, 1200);
    return () => {
      disposed = true;
      clearInterval(intervalId);
    };
  }, [
    showMetrics,
    circleProjects,
    meetingCircleKey,
  ]);

  useEffect(() => {
    if (!showMetrics) return;
    const timer = setInterval(() => {
      const stuckKeys = Object.keys(circleMetricsLoading || {}).filter((key) => circleMetricsLoading[key] === true);
      if (stuckKeys.length === 0) return;
      setCircleMetricsLoading((prev) => {
        const next = { ...prev };
        let changed = false;
        stuckKeys.forEach((key) => {
          if (next[key]) {
            next[key] = false;
            changed = true;
            metricsFetchInFlightRef.current.delete(key);
          }
        });
        return changed ? next : prev;
      });
      if (stuckKeys.length > 0) {
        setCircleMetricsError((prev) => prev || 'Relance automatique du chargement des indicateurs.');
      }
    }, 45000);
    return () => clearInterval(timer);
  }, [showMetrics, circleMetricsLoading]);

  useEffect(() => {
    let mounted = true;
    if (!selectedMeetingProjectId || !onFetchProjectObjectives) {
      setProjectObjectives([]);
      setObjectivesError(null);
      setObjectivesLoading(false);
      return () => {
        mounted = false;
      };
    }
    setObjectivesLoading(true);
    setObjectivesError(null);
    onFetchProjectObjectives(selectedMeetingProjectId)
      .then((data) => {
        if (!mounted) return;
        setProjectObjectives(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setProjectObjectives([]);
        setObjectivesError(err?.message || 'Erreur lors du chargement des objectifs');
      })
      .finally(() => {
        if (!mounted) return;
        setObjectivesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedMeetingProjectId, onFetchProjectObjectives]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedProject?.circle_id) {
      setProjectFocusEntries([]);
      setProjectFocusError(null);
      setProjectFocusLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setProjectFocusLoading(true);
    setProjectFocusError(null);
    fetch(`${apiBaseUrl}/circle-focus?parent_id=${selectedProject.circle_id}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Erreur lors du chargement des focus');
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setProjectFocusEntries(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setProjectFocusEntries([]);
        setProjectFocusError(err?.message || 'Erreur lors du chargement des focus');
      })
      .finally(() => {
        if (cancelled) return;
        setProjectFocusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProject?.circle_id, getAuthHeaders, apiBaseUrl]);

  useEffect(() => {
    if (!selectedPoint) {
      setPointNotesDraft('');
      pointNotesTypingRef.current = false;
      if (pointNotesTypingTimer.current) clearTimeout(pointNotesTypingTimer.current);
      return;
    }
    setPointNotesDraft(selectedPoint.notes || '');
    pointNotesTypingRef.current = false;
    if (pointNotesTypingTimer.current) clearTimeout(pointNotesTypingTimer.current);
  }, [selectedPoint?.id]);

  useEffect(() => {
    if (!selectedPoint) return;
    const nextNotes = selectedPoint.notes || '';
    if (pointNotesTypingRef.current) return;
    if (nextNotes !== pointNotesDraft) {
      setPointNotesDraft(nextNotes);
    }
  }, [selectedPoint?.id, selectedPoint?.notes]);

  const syncPointComposer = useCallback((nextComposer) => {
    if (!userIsSecretary || !selectedPoint || !onPointComposerUpdate) return;
    onPointComposerUpdate(nextComposer);
  }, [userIsSecretary, selectedPoint, onPointComposerUpdate]);

  const closePointComposer = useCallback(() => {
    syncPointComposer({
      mode: null,
      point_id: null,
      task_form: defaultTaskForm,
      objective_form: defaultObjectiveForm,
    });
  }, [syncPointComposer, defaultTaskForm, defaultObjectiveForm]);

  const openTaskComposer = useCallback(() => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'task',
      point_id: String(selectedPoint.id),
      task_form: defaultTaskForm,
      objective_form: defaultObjectiveForm,
    });
  }, [selectedPoint, syncPointComposer, defaultTaskForm, defaultObjectiveForm]);

  const openObjectiveComposer = useCallback(() => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'objective',
      point_id: String(selectedPoint.id),
      task_form: defaultTaskForm,
      objective_form: defaultObjectiveForm,
    });
  }, [selectedPoint, syncPointComposer, defaultTaskForm, defaultObjectiveForm]);

  const updateTaskComposer = useCallback((updates) => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'task',
      point_id: String(selectedPoint.id),
      task_form: { ...taskForm, ...updates },
      objective_form: objectiveForm,
    });
  }, [selectedPoint, syncPointComposer, taskForm, objectiveForm]);

  const updateObjectiveComposer = useCallback((updates) => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'objective',
      point_id: String(selectedPoint.id),
      task_form: taskForm,
      objective_form: { ...objectiveForm, ...updates },
    });
  }, [selectedPoint, syncPointComposer, taskForm, objectiveForm]);

  useEffect(() => {
    return () => {
      if (pointNotesTypingTimer.current) clearTimeout(pointNotesTypingTimer.current);
    };
  }, []);


  const handleAddPoint = () => {
    const trimmed = (newPointTitle || '').trim();
    if (!trimmed) return;
    onAddMeetingPoint && onAddMeetingPoint(trimmed);
    setNewPointTitle('');
  };

  const statusLabel = (status) => {
    const norm = (status || 'open').toLowerCase();
    if (norm === 'closed') return 'Clôture';
    if (norm === 'treated') return 'Traité';
    if (norm === 'converted') return 'Converti';
    return 'Ouvert';
  };
  const projectStatusLabel = (status) => {
    const norm = String(status || '').toLowerCase();
    if (norm === 'in_progress') return 'En cours';
    if (norm === 'done') return 'Termine';
    if (norm === 'blocked') return 'Bloque';
    if (norm === 'not_started') return 'Non démarré';
    return status || '';
  };

  const formatNumber = (value, decimals = 0) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0';
    return num.toLocaleString('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  const formatDateKey = useCallback((value) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  const formatFocusText = useCallback((text) => (
    (text || '').replace(focusMentionPattern, (_match, _type, _id, label) => `@${label}`)
  ), []);
  const sumProjectHours = useCallback((hoursByDay, projectId) => {
    if (!hoursByDay || projectId === undefined || projectId === null) return 0;
    let total = 0;
    Object.values(hoursByDay).forEach((projects) => {
      if (!projects) return;
      const value = projects[String(projectId)];
      if (value) total += Number(value);
    });
    return total;
  }, []);
  const parseMetricDate = useCallback((value) => {
    if (!value) return null;
    if (value instanceof Date) return new Date(value);
    if (typeof value === 'string') {
      const trimmed = value.split('T')[0];
      const [year, month, day] = trimmed.split('-').map(Number);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, []);
  const weekRange = useMemo(() => {
    const today = new Date();
    return {
      start: getIsoWeekStart(today),
      end: getIsoWeekEnd(today),
    };
  }, []);
  const weekLabel = `${weekRange.start.toLocaleDateString('fr-FR')} - ${weekRange.end.toLocaleDateString('fr-FR')}`;
  const selectedRange = useMemo(() => {
    if (!metricsRange || !metricsRangeValid) return null;
    const start = parseMetricDate(metricsRange.start);
    const end = parseMetricDate(metricsRange.end);
    if (!start || !end) return null;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    if (start > end) return null;
    return {
      start,
      end,
      label: `${start.toLocaleDateString('fr-FR')} - ${end.toLocaleDateString('fr-FR')}`,
    };
  }, [metricsRange, metricsRangeValid, parseMetricDate]);
  const activeRange = selectedRange || weekRange;
  const rangeLabel = selectedRange ? selectedRange.label : weekLabel;
  const rangeTitle = selectedRange ? 'Période sélectionnée' : 'Semaine en cours';
  const activeRangeKey = `${formatDateKey(activeRange.start)}:${formatDateKey(activeRange.end)}`;
  const metricsSource = useMemo(
    () => ({ ...(projectMetricsById || {}), ...circleMetricsById }),
    [projectMetricsById, circleMetricsById]
  );

  const projectFocusParticipants = useMemo(() => {
    if (!selectedProject?.id) return [];
    const token = `@[project:${selectedProject.id}|`;
    const grouped = new Map();
    (projectFocusEntries || []).forEach((entry) => {
      if (!entry?.focus_text || !entry.focus_text.includes(token)) return;
      if (entry.employee_id === undefined || entry.employee_id === null) return;
      const key = String(entry.employee_id);
      if (!grouped.has(key)) {
        const employee = employeeById[key];
        grouped.set(key, {
          employeeId: entry.employee_id,
          name: employee?.name || `Employe #${entry.employee_id}`,
          picture: employee?.picture || employee?.photo_url || employee?.google_picture || employee?.google_picture_url || null,
          roles: new Set(),
          focusTexts: new Set(),
        });
      }
      const target = grouped.get(key);
      const roleName = circleNameById[String(entry.circle_id)] || `Role #${entry.circle_id}`;
      if (roleName) target.roles.add(roleName);
      const cleanedFocus = formatFocusText(entry.focus_text || '');
      if (cleanedFocus) target.focusTexts.add(cleanedFocus);
    });
    const list = Array.from(grouped.values()).map((item) => ({
      ...item,
      roles: Array.from(item.roles),
      focusTexts: Array.from(item.focusTexts),
    }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [selectedProject?.id, projectFocusEntries, employeeById, circleNameById, formatFocusText]);

  const projectFocusParticipantIds = useMemo(
    () => projectFocusParticipants.map((participant) => participant.employeeId),
    [projectFocusParticipants]
  );
  const projectFocusParticipantKey = useMemo(
    () => projectFocusParticipantIds.map(String).sort().join('|'),
    [projectFocusParticipantIds]
  );

  useEffect(() => {
    let cancelled = false;
    if (!selectedProject?.id || !projectFocusParticipantIds.length || !activeRangeKey) {
      setProjectHoursByEmployee({});
      setProjectHoursLoading(false);
      setProjectHoursError(null);
      return () => {
        cancelled = true;
      };
    }
    const startDate = formatDateKey(activeRange.start);
    const endDate = formatDateKey(activeRange.end);
    setProjectHoursLoading(true);
    setProjectHoursError(null);
    Promise.all(
      projectFocusParticipantIds.map(async (employeeId) => {
        const response = await fetch(
          `${apiBaseUrl}/hours/employee/${employeeId}?start_date=${startDate}&end_date=${endDate}`,
          { headers: getAuthHeaders() }
        );
        if (!response.ok) {
          throw new Error('Erreur lors du chargement des heures');
        }
        const data = await response.json();
        return {
          employeeId,
          hours: sumProjectHours(data?.hours_by_day || {}, selectedProject.id),
        };
      })
    )
      .then((items) => {
        if (cancelled) return;
        const next = {};
        items.forEach((item) => {
          next[String(item.employeeId)] = item.hours;
        });
        setProjectHoursByEmployee(next);
      })
      .catch((err) => {
        if (cancelled) return;
        setProjectHoursByEmployee({});
        setProjectHoursError(err?.message || 'Erreur lors du chargement des heures');
      })
      .finally(() => {
        if (cancelled) return;
        setProjectHoursLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedProject?.id,
    projectFocusParticipantKey,
    activeRangeKey,
    formatDateKey,
    sumProjectHours,
    getAuthHeaders,
    apiBaseUrl,
  ]);

  const weeklyMetricsHistory = useMemo(() => {
    const today = toStartOfDay(new Date());
    const historyStart = addDaysToDate(getIsoWeekStart(today), -7 * (WEEK_HISTORY_COUNT - 1));
    const historyEnd = getIsoWeekEnd(today);
    const metricStart = toStartOfDay(historyStart);
    const metricEnd = toEndOfDay(today);
    const weeksByKey = new Map();

    for (let cursor = new Date(historyStart); cursor <= historyEnd; cursor = addDaysToDate(cursor, 7)) {
      const start = getIsoWeekStart(cursor);
      const end = getIsoWeekEnd(cursor);
      const key = formatDateKeyValue(start);
      weeksByKey.set(key, {
        key,
        start,
        end,
        hours: 0,
        expenses: 0,
        tokens: 0,
        velocitySum: 0,
        velocityCount: 0,
        hasData: false,
      });
    }

    circleProjects.forEach((project) => {
      const metrics = metricsSource[String(project.id)];
      if (!Array.isArray(metrics)) return;
      const projectStatsByWeek = new Map();

      metrics.forEach((entry) => {
        const parsedDate = parseMetricDate(entry?.date);
        if (!parsedDate) return;
        const entryDate = toStartOfDay(parsedDate);
        if (entryDate < metricStart || entryDate > metricEnd) return;

        const weekKey = formatDateKeyValue(getIsoWeekStart(entryDate));
        const week = weeksByKey.get(weekKey);
        if (!week) return;

        const entryHours = Number(entry.hours_daily || 0);
        const entryTokens = Number(entry.tokens_daily || 0);
        const entryExpenses = Number(entry.expenses_daily || 0);
        week.hours += entryHours;
        week.tokens += entryTokens;
        week.expenses += entryExpenses;
        week.hasData = true;

        const projectStats = projectStatsByWeek.get(weekKey) || { hours: 0, tokens: 0, hasData: false };
        projectStats.hours += entryHours;
        projectStats.tokens += entryTokens;
        projectStats.hasData = true;
        projectStatsByWeek.set(weekKey, projectStats);
      });

      projectStatsByWeek.forEach((stats, weekKey) => {
        const week = weeksByKey.get(weekKey);
        if (!week || !stats.hasData || stats.hours <= 0) return;
        week.velocitySum += (stats.tokens * DAILY_HOURS) / stats.hours;
        week.velocityCount += 1;
      });
    });

    return Array.from(weeksByKey.values())
      .map((week) => {
        const periodStart = week.start;
        const periodEnd = minDateValue(week.end, toEndOfDay(today));
        const denominatorEnd = week.start <= today && week.end >= today
          ? minDateValue(periodEnd, today)
          : periodEnd;
        const businessDays = countBusinessDays(periodStart, denominatorEnd);
        const daysCount = Math.max(businessDays || countCalendarDays(periodStart, denominatorEnd), 1);
        const velocity = week.velocityCount > 0 ? week.velocitySum / week.velocityCount : 0;
        return {
          ...week,
          label: `${week.start.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} - ${week.end.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}`,
          isCurrentWeek: week.start <= today && week.end >= today,
          isPartial: week.start <= today && week.end > today,
          daysCount,
          hoursPerDay: week.hours / daysCount,
          tokensPerDay: week.tokens / daysCount,
          expensesPerDay: week.expenses / daysCount,
          velocity,
        };
      })
      .sort((a, b) => b.start - a.start);
  }, [circleProjects, metricsSource, parseMetricDate, DAILY_HOURS]);

  const weeklyMetricsHasData = weeklyMetricsHistory.some((week) => week.hasData);
  const referenceWeekMetrics = weeklyMetricsHistory.find((week) => week.isCurrentWeek)
    || weeklyMetricsHistory[0]
    || null;
  const weeklyMetricsChartData = useMemo(() => (
    [...weeklyMetricsHistory]
      .sort((a, b) => a.start - b.start)
      .map((week) => ({
        week: week.start.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        label: week.label,
        heures: Number(week.hoursPerDay.toFixed(2)),
        tokens: Number(week.tokensPerDay.toFixed(2)),
        velocite: Number(week.velocity.toFixed(2)),
        depenses: Number(week.expensesPerDay.toFixed(2)),
        isCurrentWeek: week.isCurrentWeek,
      }))
  ), [weeklyMetricsHistory]);
  const weeklyMetricCharts = useMemo(() => ([
    {
      key: 'heures',
      title: 'Heures / jour',
      color: '#2563eb',
      unit: 'h/j',
      decimals: 1,
      reference: referenceWeekMetrics?.hoursPerDay || 0,
    },
    {
      key: 'tokens',
      title: 'Tokens / jour',
      color: '#16a34a',
      unit: 'tokens/j',
      decimals: 1,
      reference: referenceWeekMetrics?.tokensPerDay || 0,
    },
    {
      key: 'velocite',
      title: 'Vélocité',
      color: '#7c3aed',
      unit: 'tokens/j',
      decimals: 1,
      reference: referenceWeekMetrics?.velocity || 0,
    },
    {
      key: 'depenses',
      title: 'Dépenses / jour',
      color: '#f97316',
      unit: 'EUR/j',
      decimals: 0,
      reference: referenceWeekMetrics?.expensesPerDay || 0,
    },
  ]), [referenceWeekMetrics]);
  const circleMetricsCoverage = useMemo(() => {
    let covered = 0;
    circleProjects.forEach((project) => {
      const key = String(project.id);
      if (Object.prototype.hasOwnProperty.call(metricsSource, key)) covered += 1;
    });
    return { covered, total: circleProjects.length };
  }, [circleProjects, metricsSource]);
  const circleMetricsLoadingAny = useMemo(() => (
    circleProjects.some((project) => {
      const key = String(project.id);
      return Boolean(circleMetricsLoading[key]);
    })
  ), [circleProjects, circleMetricsLoading]);
  const circleMetricsFullyLoaded = circleMetricsCoverage.total > 0 && circleMetricsCoverage.covered === circleMetricsCoverage.total;
  const circleMetricsReady = circleMetricsCoverage.covered > 0;
  const circleMetricsErrorText = circleMetricsError || projectMetricsError;
  const circleMetricsIncomplete = circleMetricsCoverage.covered > 0 && !circleMetricsFullyLoaded;
  const circleMetricsStillLoading = circleMetricsLoadingAny || circleMetricsIncomplete;

  const activeSprintInfo = useMemo(() => {
    if (!selectedMeetingProjectId) return null;
    const sprints = projectSprints[String(selectedMeetingProjectId)] || [];
    if (!Array.isArray(sprints) || sprints.length === 0) return null;
    const ordered = [...sprints].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const active = ordered.find((sprint) => {
      if (!sprint?.start_date || !sprint?.end_date) return false;
      const start = new Date(sprint.start_date);
      const end = new Date(sprint.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    });
    if (active) return { sprint: active, label: 'Sprint en cours' };
    const upcoming = ordered.find((sprint) => {
      if (!sprint?.start_date) return false;
      const start = new Date(sprint.start_date);
      if (Number.isNaN(start.getTime())) return false;
      start.setHours(0, 0, 0, 0);
      return start > today;
    });
    if (upcoming) return { sprint: upcoming, label: 'Prochain sprint' };
    const last = [...ordered].sort((a, b) => new Date(b.end_date) - new Date(a.end_date))[0];
    return last ? { sprint: last, label: 'Dernier sprint' } : null;
  }, [selectedMeetingProjectId, projectSprints]);

  const activeSprint = activeSprintInfo?.sprint || null;
  const sprintDatesLabel = activeSprint?.start_date && activeSprint?.end_date
    ? `Du ${new Date(activeSprint.start_date).toLocaleDateString('fr-FR')} au ${new Date(activeSprint.end_date).toLocaleDateString('fr-FR')}`
    : null;

  const sprintObjectiveSummary = useMemo(() => {
    if (!activeSprint?.id) return { total: 0, done: 0, todo: 0, percent: 0 };
    const sprintObjectives = (projectObjectives || []).filter(
      (objective) => String(objective.sprint_id) === String(activeSprint.id)
    );
    const done = sprintObjectives.filter(
      (objective) => String(objective.status || '').toLowerCase() === 'done'
    ).length;
    const total = sprintObjectives.length;
    const todo = Math.max(0, total - done);
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, todo, percent };
  }, [projectObjectives, activeSprint]);

  const sprintObjectivesByStatus = useMemo(() => {
    const buckets = {
      not_started: [],
      in_progress: [],
      blocked: [],
    };
    if (!activeSprint?.id) return buckets;
    (projectObjectives || []).forEach((objective) => {
      if (String(objective.sprint_id) !== String(activeSprint.id)) return;
      const status = String(objective.status || 'not_started').toLowerCase();
      if (status === 'done') return;
      if (buckets[status]) {
        buckets[status].push(objective);
      } else {
        buckets.not_started.push(objective);
      }
    });
    return buckets;
  }, [projectObjectives, activeSprint]);

  const sprintObjectivesCount = useMemo(() => (
    sprintObjectivesByStatus.not_started.length
    + sprintObjectivesByStatus.in_progress.length
    + sprintObjectivesByStatus.blocked.length
  ), [sprintObjectivesByStatus]);

  const sprintTokenTotal = Number(activeSprint?.total_tokens || 0);
  const sprintTokenDone = Number(activeSprint?.tokens_completed || 0);
  const sprintTokenPct = sprintTokenTotal > 0
    ? Math.min(100, Math.round((sprintTokenDone / sprintTokenTotal) * 100))
    : 0;
  const selectedProjectMetrics = useMemo(() => {
    if (!selectedMeetingProjectId) {
      return { hours: 0, tokens: 0, velocity: 0, hasData: false };
    }
    const metrics = metricsSource[String(selectedMeetingProjectId)];
    if (!Array.isArray(metrics)) {
      return { hours: 0, tokens: 0, velocity: 0, hasData: false };
    }
    let hours = 0;
    let tokens = 0;
    let hasData = false;
    metrics.forEach((entry) => {
      const entryDate = parseMetricDate(entry?.date);
      if (!entryDate) return;
      if (entryDate >= activeRange.start && entryDate <= activeRange.end) {
        hours += Number(entry.hours_daily || 0);
        tokens += Number(entry.tokens_daily || 0);
        hasData = true;
      }
    });
    const velocity = hours > 0 ? (tokens * DAILY_HOURS) / hours : 0;
    return { hours, tokens, velocity, hasData };
  }, [selectedMeetingProjectId, metricsSource, parseMetricDate, activeRange.start, activeRange.end, DAILY_HOURS]);
  const selectedProjectPriorityInfo = selectedProject
    ? getProjectPriorityInfo(projectPriorityDraft, DEFAULT_PROJECT_PRIORITY_VALUE)
    : null;

  const handleProjectPriorityChange = async (event) => {
    const nextPriority = event.target.value;
    setProjectPriorityDraft(nextPriority);
    if (!selectedProject?.id || !userIsSecretary || !onUpdateProjectReview) return;

    const requestId = projectPriorityRequestRef.current + 1;
    projectPriorityRequestRef.current = requestId;
    setProjectPrioritySaveState('saving');
    const updated = await onUpdateProjectReview(selectedProject.id, {
      previous_status: currentProjectReview?.previous_status || null,
      next_milestone: currentProjectReview?.next_milestone || '',
      weekly_objective: currentProjectReview?.weekly_objective || '',
      priority: Number(nextPriority),
      notes: currentProjectReview?.notes || '',
    });
    if (projectPriorityRequestRef.current !== requestId) return;
    setProjectPrioritySaveState(updated ? 'saved' : 'error');
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-auto min-h-full overflow-visible flex flex-col">
      <div className="flex items-start justify-between mb-6 border-b border-slate-100 pb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">
            <RefreshCcw size={12} /> Réunion tactique
          </div>
          <h2 className="text-2xl font-bold text-slate-900">
            {selectedCircle?.name || 'Réunion'}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Secrétaire : {secretary?.name || 'Non défini'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Phase : {meetingPhaseInfo?.label || "Tour d'inclusion"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-2 mr-2">
            <div className="flex flex-wrap gap-2">
              {meetingParticipants.map((p, idx) => {
                const isConnected = isParticipantConnected(p.id);
                const employee = activeEmployees.find(e => String(e.id) === String(p.id));
                const avatarUrl = employee?.picture || employee?.photo_url || employee?.avatar_url;
                const firstName = p.name ? p.name.split(' ')[0] : 'Inconnu';
                const isSecretary = String(p.id) === String(activeMeeting?.created_by);
                const canTransfer = userIsSecretary && !isSecretary && isConnected && Boolean(onTransferSecretary);

                return (
                  <div
                    key={idx}
                    title={canTransfer ? 'Transferer le role de secrétaire' : p.name}
                    onClick={() => {
                      if (canTransfer) onTransferSecretary(p.id);
                    }}
                    className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${isConnected
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-slate-50 border-slate-200 text-slate-500 opacity-60'
                      } ${canTransfer ? 'cursor-pointer hover:bg-indigo-50 hover:border-indigo-200' : ''}`}
                  >
                    <div className={`relative w-5 h-5 rounded-full overflow-hidden ${isConnected ? 'ring-1 ring-emerald-500' : 'ring-1 ring-slate-300'
                      } bg-slate-200 flex-shrink-0`}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[9px] font-bold uppercase text-slate-500">
                          {p.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <span>{firstName}</span>
                  </div>
                );
              })}
            </div>
            {userIsSecretary && onInviteParticipant && (
              <div className="flex flex-wrap gap-2">
                <select
                  value={inviteEmployeeId}
                  onChange={(e) => setInviteEmployeeId(e.target.value)}
                  className="min-w-[180px] border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-100 outline-none"
                  disabled={availableInvitees.length === 0}
                >
                  <option value="">
                    {availableInvitees.length === 0 ? 'Aucune personne a inviter' : 'Inviter une personne'}
                  </option>
                  {availableInvitees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (!inviteEmployeeId) return;
                    onInviteParticipant(Number(inviteEmployeeId));
                    setInviteEmployeeId('');
                  }}
                  className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={!inviteEmployeeId}
                >
                  Inviter
                </button>
              </div>
            )}
          </div>

          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${realtimeConnected ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            <div className={`w-2 h-2 rounded-full ${realtimeConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
            {realtimeConnected ? 'En ligne' : 'Hors ligne'}
          </div>

          {userIsSecretary && (
            <button
              onClick={onCloseMeeting}
              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors"
            >
              Clôturer
            </button>
          )}
        </div>
      </div>

      {meetingError && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
          <AlertCircle size={16} />
          {meetingError}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 h-full overflow-visible">
        <div className="w-full lg:w-96 shrink-0 flex flex-col gap-4 h-full overflow-y-auto pr-2">
          {showProjectSidebar && (
            <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Projets</h3>
                <span className="text-[10px] text-slate-400">{meetingProjects.length}</span>
              </div>
              {meetingProjects.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Aucun projet en cours.</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {meetingProjects.map((project) => {
                    const isSelected = String(selectedMeetingProjectId) === String(project.id);
                    const projectPriorityInfo = getMeetingProjectPriorityInfo(project);
                    return (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => {
                          if (!userIsSecretary || !onSelectMeetingProject) return;
                          onSelectMeetingProject(isSelected ? null : project.id);
                        }}
                        className={`w-full text-left p-2 rounded-lg border text-xs transition-colors ${isSelected
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-200'
                          } ${userIsSecretary ? 'cursor-pointer' : 'cursor-default'}`}
                        disabled={!userIsSecretary}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold truncate">{project.name}</span>
                          <div className="flex shrink-0 items-center gap-1">
                            {projectPriorityInfo && (
                              <span
                                className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${projectPriorityInfo.className}`}
                                title={`Priorite projet : P${projectPriorityInfo.value} - ${projectPriorityInfo.label}`}
                              >
                                P{projectPriorityInfo.value}
                              </span>
                            )}
                            {project.project_number !== undefined && project.project_number !== null && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">
                                #{project.project_number}
                              </span>
                            )}
                          </div>
                        </div>
                        {project.status && (
                          <div className="text-[10px] text-slate-400 mt-1">Statut: {projectStatusLabel(project.status)}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {!userIsSecretary && meetingProjects.length > 0 && (
                <p className="text-[10px] text-slate-400">Seul le secrétaire choisit le projet.</p>
              )}
            </div>
          )}

            <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Importer un point</h3>
                  <span className="text-[10px] text-slate-400">
                    {userIsSecretary ? 'Points connectés' : 'Vos points'}
                  </span>
                </div>
                <select
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-100 outline-none"
                  value={preparedPointId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPreparedPointId(val);
                    if (val) {
                      onImportMeetingPoint && onImportMeetingPoint(Number(val));
                      setPreparedPointId('');
                    }
                  }}
                  disabled={preparedPointsLoading || preparedPoints.length === 0}
                >
                  <option value="">Choisir un point</option>
                  {preparedPoints.map((point) => (
                    <option key={point.id} value={point.id}>
                      {userIsSecretary && point.author_name
                        ? `${point.title} - ${point.author_name}`
                        : point.title}
                    </option>
                  ))}
                </select>
                {preparedPointsLoading ? (
                  <p className="text-xs text-slate-400 italic">Chargement des points...</p>
                ) : preparedPoints.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucun point prepare.</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Points</h3>
                  <span className="text-[10px] text-slate-400">{visibleMeetingPoints.length}</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPointTitle}
                    onChange={(e) => setNewPointTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddPoint();
                    }}
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-100 outline-none"
                    placeholder="Ajouter un point..."
                  />
                  <button
                    onClick={handleAddPoint}
                    className="px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    disabled={!newPointTitle.trim()}
                  >
                    Ajouter
                  </button>
                </div>
                {visibleMeetingPoints.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucun point pour le moment.</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {visibleMeetingPoints.map((point) => {
                      const isSelected = String(selectedMeetingPointId) === String(point.id);
                      const isDone = ['treated', 'converted', 'closed'].includes((point.status || '').toLowerCase());
                      return (
                        <div
                          key={point.id}
                          onClick={() => userIsSecretary && onSelectMeetingPoint && onSelectMeetingPoint(point.id)}
                          className={`p-2 rounded-lg border text-sm transition-colors ${isSelected ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'} ${userIsSecretary ? 'cursor-pointer' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className={`font-medium ${isDone ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                              {point.title}
                            </div>
                            <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                              {statusLabel(point.status)}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Par {point.author_name || 'un membre'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phases</h3>
              <span className="text-[10px] text-slate-400">{meetingPhaseInfo?.badge || 'Inclusion'}</span>
            </div>
            <div className="space-y-2">
              {meetingPhases.map((phase) => {
                const isActive = effectiveMeetingPhase === phase.key;
                return (
                  <button
                    key={phase.key}
                    type="button"
                    onClick={() => {
                      if (!userIsSecretary) return;
                      onSelectMeetingPhase && onSelectMeetingPhase(phase.key);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${isActive
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-200'
                      } ${userIsSecretary ? 'cursor-pointer' : 'cursor-default opacity-70'}`}
                    disabled={!userIsSecretary}
                  >
                    <div className="font-semibold">{phase.label}</div>
                    <div className="text-[10px] text-slate-400">{phase.helper}</div>
                  </button>
                );
              })}
            </div>
            {!userIsSecretary && (
              <p className="text-[10px] text-slate-400">Le secrétaire pilote la phase en cours.</p>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col h-full overflow-y-auto bg-slate-50/50 rounded-xl border border-slate-200">
          <div className="flex-1 h-auto overflow-visible p-6">
            <div className="w-full max-w-6xl mx-auto space-y-6">
              {showInclusion && (
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tour d'inclusion</p>
                    <h3 className="text-xl font-bold text-slate-800 mt-1">Disponibilité des participants</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Chaque personne partage un mot pour dire qu'elle est disponible.
                    </p>
                  </div>
                  {meetingParticipants.length === 0 ? (
                    <p className="text-sm text-slate-400">Aucun participant connecté.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {meetingParticipants.map((participant) => {
                        const isSecretary = String(participant.id) === String(activeMeeting?.created_by);
                        const isConnected = isParticipantConnected(participant.id);
                        return (
                          <div
                            key={participant.id || participant.name}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${isConnected
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : 'bg-slate-50 border-slate-200 text-slate-500'
                              }`}
                          >
                            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            <span className="font-semibold">{participant.name}</span>
                            {isSecretary && (
                              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                                Secrétaire
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {showMetrics && (
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Indicateurs du cercle</p>
                      <h3 className="text-xl font-bold text-slate-800 mt-1">
                        {selectedCircle?.name || 'Cercle'}
                      </h3>
                      <p className="text-xs text-slate-400 mt-1">
                        Historique hebdomadaire : 5 semaines précédentes + semaine en cours
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">Inclut les sous-cercles</p>
                    </div>
                    <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                      {circleProjects.length} projets
                    </span>
                  </div>

                  {!meetingCircleId ? (
                    <p className="text-sm text-slate-400">Sélectionnez un cercle pour afficher les indicateurs.</p>
                  ) : circleProjects.length === 0 ? (
                    <p className="text-sm text-slate-400">Aucun projet associé a ce cercle.</p>
                  ) : !circleMetricsReady && circleMetricsLoadingAny ? (
                    <p className="text-sm text-slate-400">Chargement des indicateurs...</p>
                  ) : !circleMetricsReady && circleMetricsErrorText ? (
                    <p className="text-sm text-red-500">{circleMetricsErrorText}</p>
                  ) : !circleMetricsReady ? (
                    <p className="text-sm text-slate-400">Aucune donnée disponible pour les semaines suivies.</p>
                  ) : !weeklyMetricsHasData && circleMetricsStillLoading ? (
                    <p className="text-sm text-slate-400">
                      Chargement des indicateurs... {circleMetricsCoverage.covered}/{circleMetricsCoverage.total} projets parcourus.
                    </p>
                  ) : !weeklyMetricsHasData ? (
                    <p className="text-sm text-slate-400">Aucune donnée disponible pour les semaines suivies.</p>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold text-slate-600">
                            Référence : {referenceWeekMetrics?.label || rangeLabel}
                            {referenceWeekMetrics?.isPartial ? ' (semaine en cours)' : ''}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            5 semaines précédentes + semaine en cours
                          </p>
                        </div>
                        <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                          Moyenne par jour ouvré
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Heures / jour</p>
                          <p className="text-lg font-bold text-slate-800">{formatNumber(referenceWeekMetrics?.hoursPerDay, 1)} h/j</p>
                          <p className="text-xs text-slate-500">{formatNumber(referenceWeekMetrics?.hours, 1)} h total</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Tokens / jour</p>
                          <p className="text-lg font-bold text-slate-800">{formatNumber(referenceWeekMetrics?.tokensPerDay, 1)} tokens/j</p>
                          <p className="text-xs text-slate-500">{formatNumber(referenceWeekMetrics?.tokens, 0)} tokens total</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Vélocité moyenne</p>
                          <p className="text-lg font-bold text-slate-800">{formatNumber(referenceWeekMetrics?.velocity, 1)} tokens/j</p>
                          <p className="text-xs text-slate-500">Calculée avec les heures</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Dépenses / jour</p>
                          <p className="text-lg font-bold text-slate-800">{formatNumber(referenceWeekMetrics?.expensesPerDay, 0)} EUR/j</p>
                          <p className="text-xs text-slate-500">{formatNumber(referenceWeekMetrics?.expenses, 0)} EUR total</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {weeklyMetricCharts.map((chart) => (
                          <div key={chart.key} className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div>
                                <p className="text-[10px] uppercase text-slate-400 font-semibold">{chart.title}</p>
                                <p className="text-xs text-slate-500">Moyenne journalière par semaine</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-slate-800">
                                  {formatNumber(chart.reference, chart.decimals)} {chart.unit}
                                </p>
                                <p className="text-[10px] text-slate-400">Semaine en cours</p>
                              </div>
                            </div>
                            <div className="h-52 min-h-52">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={weeklyMetricsChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                  <XAxis
                                    dataKey="week"
                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                    axisLine={{ stroke: '#cbd5e1' }}
                                    tickLine={{ stroke: '#cbd5e1' }}
                                  />
                                  <YAxis
                                    width={42}
                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                    axisLine={{ stroke: '#cbd5e1' }}
                                    tickLine={{ stroke: '#cbd5e1' }}
                                    tickFormatter={(value) => formatNumber(value, chart.decimals)}
                                  />
                                  <Tooltip
                                    labelFormatter={(_label, payload) => payload?.[0]?.payload?.label || _label}
                                    formatter={(value) => [
                                      `${formatNumber(value, chart.decimals)} ${chart.unit}`,
                                      chart.title,
                                    ]}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey={chart.key}
                                    name={chart.title}
                                    stroke={chart.color}
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    activeDot={{ r: 5 }}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        ))}
                      </div>

                      {false && (
                      <div className="hidden">
                        <div className="h-72 min-h-72">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={weeklyMetricsChartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis
                                dataKey="week"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={{ stroke: '#cbd5e1' }}
                                tickLine={{ stroke: '#cbd5e1' }}
                              />
                              <YAxis
                                yAxisId="activity"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={{ stroke: '#cbd5e1' }}
                                tickLine={{ stroke: '#cbd5e1' }}
                              />
                              <YAxis
                                yAxisId="expenses"
                                orientation="right"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={{ stroke: '#cbd5e1' }}
                                tickLine={{ stroke: '#cbd5e1' }}
                              />
                              <Tooltip
                                labelFormatter={(_label, payload) => payload?.[0]?.payload?.label || _label}
                                formatter={(value, name) => {
                                  const decimals = name === 'Dépenses / j' ? 0 : 1;
                                  const suffix = name === 'Heures / j'
                                    ? ' h/j'
                                    : name === 'Dépenses / j'
                                      ? ' EUR/j'
                                      : ' tokens/j';
                                  return [`${formatNumber(value, decimals)}${suffix}`, name];
                                }}
                              />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              <Line
                                yAxisId="activity"
                                type="monotone"
                                dataKey="heures"
                                name="Heures / j"
                                stroke="#2563eb"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                              <Line
                                yAxisId="activity"
                                type="monotone"
                                dataKey="tokens"
                                name="Tokens / j"
                                stroke="#16a34a"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                              <Line
                                yAxisId="activity"
                                type="monotone"
                                dataKey="velocite"
                                name="Vélocité"
                                stroke="#7c3aed"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                              <Line
                                yAxisId="expenses"
                                type="monotone"
                                dataKey="depenses"
                                name="Dépenses / j"
                                stroke="#f97316"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      )}

                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-left text-sm">
                          <thead className="bg-slate-50 text-[10px] uppercase text-slate-400">
                            <tr>
                              <th className="px-3 py-2 font-semibold">Semaine</th>
                              <th className="px-3 py-2 font-semibold">Heures / j</th>
                              <th className="px-3 py-2 font-semibold">Tokens / j</th>
                              <th className="px-3 py-2 font-semibold">Vélocité</th>
                              <th className="px-3 py-2 font-semibold">Dépenses / j</th>
                              <th className="px-3 py-2 font-semibold text-right">Jours</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {weeklyMetricsHistory.map((week) => (
                              <tr
                                key={week.key}
                                className={week.isCurrentWeek ? 'bg-indigo-50/50' : 'bg-white'}
                              >
                                <td className="px-3 py-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-semibold text-slate-700">{week.label}</span>
                                    {week.isCurrentWeek && (
                                      <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                        En cours
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold text-slate-800">{formatNumber(week.hoursPerDay, 1)} h</div>
                                  <div className="text-[10px] text-slate-400">{formatNumber(week.hours, 1)} h total</div>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold text-slate-800">{formatNumber(week.tokensPerDay, 1)}</div>
                                  <div className="text-[10px] text-slate-400">{formatNumber(week.tokens, 0)} total</div>
                                </td>
                                <td className="px-3 py-2 font-semibold text-slate-800">
                                  {formatNumber(week.velocity, 1)}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold text-slate-800">{formatNumber(week.expensesPerDay, 0)} EUR</div>
                                  <div className="text-[10px] text-slate-400">{formatNumber(week.expenses, 0)} EUR total</div>
                                </td>
                                <td className="px-3 py-2 text-right text-xs text-slate-500">
                                  {week.daysCount} j
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {circleMetricsCoverage.covered}/{circleMetricsCoverage.total} projets suivis
                        {circleMetricsLoadingAny ? ' (chargement en cours)' : ''}
                      </div>
                      {circleMetricsIncomplete && (
                        <p className="text-xs text-slate-500">Données partielles tant que tous les projets ne sont pas chargés.</p>
                      )}
                      {circleMetricsErrorText && (
                        <p className="text-xs text-red-500">{circleMetricsErrorText}</p>
                      )}
                    </>
                  )}
                </div>
              )}
              {showProjects && (
                <>
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Revue des projets</p>
                        <h3 className="text-xl font-bold text-slate-800 mt-1">
                          {selectedProject?.name || 'Sélectionnez un projet'}
                        </h3>
                        {selectedProject?.status && (
                          <p className="text-xs text-slate-400 mt-1">
                            Statut : {projectStatusLabel(selectedProject.status)}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap items-end justify-end gap-2">
                        {selectedProject && (
                          <label className="block">
                            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              Priorité du projet
                            </span>
                            <select
                              value={projectPriorityDraft}
                              onChange={handleProjectPriorityChange}
                              disabled={!userIsSecretary || projectPrioritySaveState === 'saving'}
                              className="mt-1 min-w-[150px] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-500"
                              title={selectedProjectPriorityInfo
                                ? `Priorité projet : P${selectedProjectPriorityInfo.value} - ${selectedProjectPriorityInfo.label}`
                                : 'Priorité du projet'}
                            >
                              {PROJECT_PRIORITY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  P{option.value} - {option.label}
                                </option>
                              ))}
                            </select>
                            {userIsSecretary && projectPrioritySaveState !== 'idle' && (
                              <span className={`mt-1 block text-[10px] ${projectPrioritySaveState === 'error' ? 'text-rose-500' : projectPrioritySaveState === 'saved' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {projectPrioritySaveState === 'saving'
                                  ? 'Enregistrement...'
                                  : projectPrioritySaveState === 'saved'
                                    ? 'Priorité enregistrée'
                                    : "Erreur d'enregistrement"}
                              </span>
                            )}
                          </label>
                        )}
                        {selectedProject?.project_number !== undefined && selectedProject?.project_number !== null && (
                          <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                            #{selectedProject.project_number}
                          </span>
                        )}
                      </div>
                    </div>

                    {!selectedMeetingProjectId ? (
                      <p className="text-sm text-slate-400">Choisissez un projet pour demarrer la revue.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Sprint</p>
                          <p className="text-sm font-semibold text-slate-800">
                            {activeSprint
                              ? activeSprint.description || activeSprint.name || `Sprint ${activeSprint.id}`
                              : 'Aucun sprint en cours'}
                          </p>
                          {activeSprint && sprintDatesLabel && (
                            <p className="text-[10px] text-slate-400 mt-1">{sprintDatesLabel}</p>
                          )}
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Objectifs</p>
                          {activeSprint ? (
                            <>
                              <p className="text-lg font-bold text-slate-800">
                                {sprintObjectiveSummary.done} / {sprintObjectiveSummary.total}
                              </p>
                              <p className="text-xs text-slate-500">
                                {sprintObjectiveSummary.todo} a faire
                              </p>
                            </>
                          ) : (
                            <p className="text-sm text-slate-400">Aucun objectif actif.</p>
                          )}
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Tokens sprint</p>
                          {activeSprint ? (
                            <>
                              <p className="text-lg font-bold text-slate-800">
                                {formatNumber(sprintTokenDone)} / {formatNumber(sprintTokenTotal)}
                              </p>
                              <p className="text-xs text-slate-500">{sprintTokenPct}% realise</p>
                            </>
                          ) : (
                            <p className="text-sm text-slate-400">Aucun sprint en cours.</p>
                          )}
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Velocite projet</p>
                          {selectedProjectMetrics.hasData ? (
                            <>
                              <p className="text-lg font-bold text-slate-800">
                                {formatNumber(selectedProjectMetrics.velocity, 1)} tokens/j
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatNumber(selectedProjectMetrics.tokens)} tokens / {formatNumber(selectedProjectMetrics.hours, 1)} h
                              </p>
                              <p className="text-[10px] text-slate-400">Calculee sur {rangeLabel}</p>
                            </>
                          ) : (
                            <p className="text-sm text-slate-400">Aucune donnée sur la période.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Participants (focus)</p>
                        <p className="text-sm text-slate-500 mt-1">Heures saisies sur {rangeLabel}</p>
                      </div>
                      {selectedMeetingProjectId && (
                        <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                          {projectFocusParticipants.length} personnes
                        </span>
                      )}
                    </div>

                    {!selectedMeetingProjectId ? (
                      <p className="text-sm text-slate-400">Choisissez un projet pour afficher les participants.</p>
                    ) : projectFocusLoading ? (
                      <p className="text-sm text-slate-400">Chargement des focus...</p>
                    ) : projectFocusError ? (
                      <p className="text-sm text-red-500">{projectFocusError}</p>
                    ) : projectFocusParticipants.length === 0 ? (
                      <p className="text-sm text-slate-400">Aucun participant avec focus sur ce projet.</p>
                    ) : (
                      <div className="space-y-2">
                        {projectHoursLoading && (
                          <p className="text-xs text-slate-400">Chargement des heures...</p>
                        )}
                        {projectHoursError && (
                          <p className="text-xs text-red-500">{projectHoursError}</p>
                        )}
                        {projectFocusParticipants.map((participant) => {
                          const hoursValue = projectHoursByEmployee[String(participant.employeeId)] || 0;
                          const hoursLabel = projectHoursLoading ? '...' : `${formatNumber(hoursValue, 1)} h`;
                          const initials = participant.name
                            ? participant.name.split(' ').map((part) => part[0]).join('').toUpperCase()
                            : '?';
                          return (
                            <div
                              key={participant.employeeId}
                              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                {participant.picture ? (
                                  <img
                                    src={participant.picture}
                                    alt={participant.name}
                                    className="h-8 w-8 rounded-full border border-slate-200 object-cover"
                                  />
                                ) : (
                                  <div className="h-8 w-8 rounded-full bg-slate-200 text-[10px] font-semibold text-slate-600 flex items-center justify-center">
                                    {initials}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-800 truncate">{participant.name}</p>
                                  {participant.roles.length > 0 && (
                                    <p className="text-[10px] text-slate-400 uppercase tracking-wide truncate">
                                      {participant.roles.join(' / ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-slate-800">{hoursLabel}</p>
                                <p className="text-[10px] text-slate-400">{rangeTitle}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tâches du sprint</p>
                      {activeSprint?.id && (
                        <span className="text-[10px] text-slate-400">
                          {sprintObjectivesCount} tâches
                        </span>
                      )}
                    </div>
                    {!selectedMeetingProjectId ? (
                      <p className="text-sm text-slate-400">Choisissez un projet pour afficher les tâches.</p>
                    ) : !activeSprint ? (
                      <p className="text-sm text-slate-400">Aucun sprint en cours pour ce projet.</p>
                    ) : objectivesLoading ? (
                      <p className="text-sm text-slate-400">Chargement des tâches...</p>
                    ) : objectivesError ? (
                      <p className="text-sm text-red-500">{objectivesError}</p>
                    ) : sprintObjectivesCount === 0 ? (
                      <p className="text-sm text-slate-400">Aucune tâche en cours pour ce sprint.</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                          { key: 'not_started', title: 'A faire', badge: 'bg-slate-100 text-slate-600', items: sprintObjectivesByStatus.not_started },
                          { key: 'in_progress', title: 'En cours', badge: 'bg-indigo-100 text-indigo-700', items: sprintObjectivesByStatus.in_progress },
                          { key: 'blocked', title: 'Bloque', badge: 'bg-rose-100 text-rose-700', items: sprintObjectivesByStatus.blocked },
                        ].map((col) => (
                          <div key={col.key} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${col.badge}`}>
                                {col.title}
                              </span>
                              <span className="text-[10px] text-slate-400">{col.items.length}</span>
                            </div>
                            {col.items.length === 0 ? (
                              <p className="text-xs text-slate-400 italic">Aucune tâche</p>
                            ) : (
                              <div className="space-y-1">
                                {col.items.slice(0, 6).map((objective) => (
                                  <div key={objective.id} className="text-xs text-slate-600 truncate">
                                    {objective.name}
                                  </div>
                                ))}
                                {col.items.length > 6 && (
                                  <div className="text-[10px] text-slate-400">+{col.items.length - 6} autres</div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {showAgenda && (
                <>
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Création de l'ordre du jour</p>
                        <h3 className="text-xl font-bold text-slate-800 mt-1">Points a traiter</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          Ajoutez ou importez des points, puis sélectionnez-les pour les traiter.
                        </p>
                      </div>
                      <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                        {visibleMeetingPoints.length} points
                      </span>
                    </div>
                  </div>

                  {selectedPoint ? (
                    <>
                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-800">{selectedPoint.title}</h3>
                            <p className="text-xs text-slate-500 mt-1">Propose par {selectedPoint.author_name || 'un membre'}</p>
                          </div>
                          <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                            {statusLabel(selectedPoint.status)}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Notes du point</h4>
                          <span className="text-[10px] text-slate-400">Partagées</span>
                        </div>
                        <textarea
                          value={pointNotesDraft}
                          onChange={(e) => {
                            const next = e.target.value;
                            setPointNotesDraft(next);
                            pointNotesTypingRef.current = true;
                            if (pointNotesTypingTimer.current) clearTimeout(pointNotesTypingTimer.current);
                            pointNotesTypingTimer.current = setTimeout(() => {
                              pointNotesTypingRef.current = false;
                            }, 500);
                            onPointNotesChange && onPointNotesChange(selectedPoint.id, next);
                          }}
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[140px] focus:ring-2 focus:ring-indigo-100 outline-none"
                          placeholder="Notes pour ce point..."
                        />
                      </div>

                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                        <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Actions prises</h4>
                        {selectedPointActions.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Aucune action enregistrée.</p>
                        ) : (
                          <div className="space-y-2">
                            {selectedPointActions.map((action, idx) => {
                              const type = (action?.type || '').toLowerCase();
                              return (
                                <div key={`${type}-${idx}`} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                                  <div className="text-sm font-semibold text-slate-800">
                                    {type === 'objective' ? 'Objectif' : type === 'task' ? 'Tâche' : 'Action'} : {action?.name || 'Sans titre'}
                                  </div>
                                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2">
                                    {action?.project_id && (
                                      <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200">
                                        Projet: {getProjectName(action.project_id)}
                                      </span>
                                    )}
                                    {action?.owner_id && (
                                      <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200">
                                        Owner: {getEmployeeName(action.owner_id)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {userIsSecretary && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            <button
                              onClick={openTaskComposer}
                              className="px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                              Créer une tâche
                            </button>
                            <button
                              onClick={openObjectiveComposer}
                              className="px-3 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                            >
                              Créer un objectif
                            </button>
                          </div>
                        )}

                        {userIsSecretary && (selectedPoint.status || 'open').toLowerCase() === 'open' && (
                          <div className="flex justify-end pt-2">
                            <button
                              onClick={() => onPointDecision && onPointDecision(selectedPoint.id, 'treated')}
                              className="px-4 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg hover:bg-emerald-100"
                            >
                              Clore le point
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                        <ArrowRight size={24} className="text-slate-300" />
                      </div>
                      <p>Sélectionnez un point pour voir les détails</p>
                    </div>
                  )}
                </>
              )}

              {showClosure && (
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Clôture</p>
                  <h3 className="text-xl font-bold text-slate-800">Fin de réunion</h3>
                  <p className="text-sm text-slate-500">
                    Verifiez que tous les points sont traités et que les actions sont notees.
                  </p>
                  {userIsSecretary ? (
                    <button
                      onClick={onCloseMeeting}
                      className="px-4 py-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100"
                    >
                      Clôturer la réunion
                    </button>
                  ) : (
                    <p className="text-xs text-slate-400">Le secrétaire clôture la réunion.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden relative">
            {userIsSecretary && (
              <button
                className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                onClick={closePointComposer}
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            )}
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Nouvelle tache</p>
                {!userIsSecretary && (
                  <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold">
                    Saisie en direct
                  </span>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Nom</label>
                  <input
                    type="text"
                    value={taskForm.name}
                    onChange={(e) => updateTaskComposer({ name: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Nom de la tache"
                    disabled={!userIsSecretary}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Assigner a</label>
                  <select
                    value={taskForm.owner_id}
                    onChange={(e) => updateTaskComposer({ owner_id: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    disabled={!userIsSecretary}
                  >
                    <option value="">Choisir une personne</option>
                    {activeEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {userIsSecretary ? (
                  <>
                    <button
                      onClick={closePointComposer}
                      className="px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={async () => {
                        if (!taskForm.name.trim() || !taskForm.owner_id) return;
                        const created = await (onCreatePointTask && onCreatePointTask(selectedPoint.id, {
                          name: taskForm.name.trim(),
                          owner_id: Number(taskForm.owner_id),
                          project_id: selectedMeetingProjectId ? Number(selectedMeetingProjectId) : null,
                        }));
                        if (created) closePointComposer();
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      disabled={!taskForm.name.trim() || !taskForm.owner_id}
                    >
                      Creer
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-slate-400">Le secretaire saisit cette action.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showObjectiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden relative">
            {userIsSecretary && (
              <button
                className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                onClick={closePointComposer}
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            )}
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Nouvel objectif</p>
                {!userIsSecretary && (
                  <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                    Saisie en direct
                  </span>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Nom</label>
                  <input
                    type="text"
                    value={objectiveForm.name}
                    onChange={(e) => updateObjectiveComposer({ name: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Nom de l'objectif"
                    disabled={!userIsSecretary}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Projet</label>
                  <input
                    type="text"
                    list={objectiveProjectDatalistId}
                    value={objectiveProjectSearch}
                    onChange={(e) => {
                      const nextValue = e.target.value || '';
                      setObjectiveProjectSearch(nextValue);
                      const normalized = nextValue.trim().toLowerCase();
                      const exactMatch = objectiveProjectOptions.find(
                        (option) => option.label.toLowerCase() === normalized
                      );
                      if (exactMatch) {
                        if (String(objectiveForm.project_id || '') !== String(exactMatch.id)) {
                          updateObjectiveComposer({ project_id: String(exactMatch.id) });
                        }
                        return;
                      }
                      if (!normalized) {
                        if (objectiveForm.project_id) updateObjectiveComposer({ project_id: '' });
                        return;
                      }
                      if (objectiveForm.project_id && selectedObjectiveProjectLabel && nextValue !== selectedObjectiveProjectLabel) {
                        updateObjectiveComposer({ project_id: '' });
                      }
                    }}
                    onBlur={() => {
                      if (!objectiveForm.project_id || !selectedObjectiveProjectLabel) return;
                      if (objectiveProjectSearch !== selectedObjectiveProjectLabel) {
                        setObjectiveProjectSearch(selectedObjectiveProjectLabel);
                      }
                    }}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Rechercher et selectionner un projet..."
                    disabled={!userIsSecretary}
                  />
                  <datalist id={objectiveProjectDatalistId}>
                    {objectiveProjectOptions.map((option) => (
                      <option key={option.id} value={option.label} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Owner (optionnel)</label>
                  <select
                    value={objectiveForm.owner_id}
                    onChange={(e) => updateObjectiveComposer({ owner_id: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    disabled={!userIsSecretary}
                  >
                    <option value="">Choisir une personne</option>
                    {activeEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Tokens</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={objectiveForm.effort}
                    onChange={(e) => updateObjectiveComposer({ effort: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Ex: 5"
                    disabled={!userIsSecretary}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Description (optionnel)</label>
                  <textarea
                    value={objectiveForm.description}
                    onChange={(e) => updateObjectiveComposer({ description: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[90px]"
                    disabled={!userIsSecretary}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {userIsSecretary ? (
                  <>
                    <button
                      onClick={closePointComposer}
                      className="px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={async () => {
                        if (!objectiveForm.name.trim() || !objectiveForm.project_id) return;
                        const effortValue = Number.parseInt(objectiveForm.effort, 10);
                        const created = await (onCreatePointObjective && onCreatePointObjective(selectedPoint.id, {
                          name: objectiveForm.name.trim(),
                          project_id: Number(objectiveForm.project_id),
                          owner_id: objectiveForm.owner_id ? Number(objectiveForm.owner_id) : null,
                          effort: Number.isInteger(effortValue) && effortValue >= 0 ? effortValue : 1,
                          description: objectiveForm.description || '',
                        }));
                        if (created) closePointComposer();
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                      disabled={!objectiveForm.name.trim() || !objectiveForm.project_id}
                    >
                      Creer
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-slate-400">Le secretaire saisit cette action.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingPanelTactical;
